import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useAuth } from "../App";
import { Plus, GraduationCap, Clock, CheckCircle, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";

const TrainingPage = () => {
  const { t } = useTranslation('modules');
  const { isAdmin } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    department: "Quality",
    due_date: ""
  });

  const fetchTrainings = async () => {
    try {
      let url = `${API}/training`;
      if (filter !== "all") url += `?status=${filter}`;
      const response = await axios.get(url, { withCredentials: true });
      setTrainings(response.data);
    } catch (error) {
      console.error("Error fetching trainings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainings();
  }, [filter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/training`, formData, { withCredentials: true });
      setShowModal(false);
      setFormData({ title: "", description: "", department: "Quality", due_date: "" });
      fetchTrainings();
    } catch (error) {
      console.error("Error creating training:", error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle size={16} className="text-[#4ADE80]" />;
      case "in-progress": return <Clock size={16} className="text-[#FBBF24]" />;
      default: return <Calendar size={16} className="text-[#36A9E1]" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: "badge-blue",
      "in-progress": "badge-amber",
      completed: "badge-green"
    };
    return badges[status] || "badge-gray";
  };

  // Department statistics
  const departments = ["Quality", "Engineering", "Production", "IT", "All"];
  const departmentStats = departments.map(dept => ({
    name: dept,
    total: trainings.filter(item => item.department === dept || dept === "All").length,
    completed: trainings.filter(item => (item.department === dept || dept === "All") && item.status === "completed").length
  }));

  return (
    <QMSLayout title={t('modules:training.title')}>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="training-kpis">
        <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
          <p className="kpi-label">{t('training.totalPrograms')}</p>
          <p className="kpi-value">{trainings.length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#4ADE80" }}>
          <p className="kpi-label">{t('training.completed')}</p>
          <p className="kpi-value">{trainings.filter(item => item.status === "completed").length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#FBBF24" }}>
          <p className="kpi-label">{t('training.inProgress')}</p>
          <p className="kpi-value">{trainings.filter(item => item.status === "in-progress").length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#38BDF8" }}>
          <p className="kpi-label">{t('training.avgCompletion')}</p>
          <p className="kpi-value">
            {trainings.length ? Math.round(trainings.reduce((a, item) => a + (item.completion_percentage || 0), 0) / trainings.length) : 0}%
          </p>
        </div>
      </div>

      {/* Department Compliance Bars */}
      <div className="qms-card mb-6" data-testid="dept-compliance">
        <div className="qms-card-header">{t('training.deptCompliance')}</div>
        <div className="qms-card-body space-y-4">
          {departmentStats.filter(d => d.name !== "All" && d.total > 0).map((dept) => (
            <div key={dept.name}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-white">{dept.name}</span>
                <span className="text-[#8B95A5]">{dept.completed}/{dept.total} {t('training.completedOf')}</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-bar-fill"
                  style={{ width: `${dept.total ? (dept.completed / dept.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="filter-pills">
          {["all", "scheduled", "in-progress", "completed"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`filter-pill ${filter === status ? "active" : ""}`}
            >
              {status === "all" ? t('training.all') : status.replace("-", " ")}
            </button>
          ))}
        </div>

        {isAdmin && (
          <button onClick={() => setShowModal(true)} className="btn-primary" data-testid="create-training-btn">
            <Plus size={18} /> {t('training.newTraining')}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="qms-card overflow-hidden" data-testid="training-table">
        <div className="overflow-x-auto">
          <table className="qms-table">
            <thead>
              <tr>
                <th>{t('training.id')}</th>
                <th>{t('training.titleLabel')}</th>
                <th>{t('training.department')}</th>
                <th>{t('training.status')}</th>
                <th>{t('training.progress')}</th>
                <th>{t('training.dueDate')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-[#8B95A5]">{t('training.loading')}</td></tr>
              ) : trainings.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-[#8B95A5]">{t('training.noPrograms')}</td></tr>
              ) : (
                trainings.map((training) => (
                  <tr key={training.training_id}>
                    <td className="font-mono text-[#38BDF8]">{training.training_id}</td>
                    <td>
                      <div>
                        <p className="text-white">{training.title}</p>
                        {training.description && (
                          <p className="text-[#8B95A5] text-xs truncate max-w-xs">{training.description}</p>
                        )}
                      </div>
                    </td>
                    <td><span className="badge badge-blue">{training.department}</span></td>
                    <td>
                      <span className={`badge ${getStatusBadge(training.status)} flex items-center gap-1 w-fit capitalize`}>
                        {getStatusIcon(training.status)}
                        {training.status.replace("-", " ")}
                      </span>
                    </td>
                    <td>
                      <div className="w-24">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-[#8B95A5]">{training.completion_percentage}%</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-bar-fill" style={{ width: `${training.completion_percentage}%` }} />
                        </div>
                      </div>
                    </td>
                    <td>{training.due_date}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.07)]">
              <h3 className="font-['Syne'] text-lg font-semibold text-white">{t('training.newProgram')}</h3>
              <button onClick={() => setShowModal(false)} className="text-[#8B95A5] hover:text-white">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('training.titleLabel')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="qms-input"
                  required
                />
              </div>
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('training.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="qms-input min-h-[80px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('training.department')}</label>
                  <select
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="qms-select"
                  >
                    <option value="Quality">Quality</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Production">Production</option>
                    <option value="IT">IT</option>
                    <option value="All">All Departments</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('training.dueDate')}</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="qms-input"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">{t('training.createTraining')}</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">{t('common:actions.cancel', { ns: 'common' })}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default TrainingPage;
